import { Box, Tab, Tabs } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useEffect, useState } from 'react';
import { DateRangeButtons } from '../../components/DateRangeButtons';
import StyledTextField from '../../components/styled/StyledTextField';
import { t } from '../../constants/translations';
import { useLocale } from '../../contexts/locale.context';
import { getDayTimeRange, getMonthTimeRange, getWeekTimeRange } from '../../utils/dates';
import getDayDateRange from '../../utils/dates/getDayDateRange';
import useMedia from '../../utils/useMedia';
import { NivoProvider } from './NivoProvider';
import ReportByDishes from './ReportByDishes';
import ReportBySales from './ReportBySales';
import ReportByStores from './ReportByStores/ReportByStores';
import ReportByWallets from './ReportByWallets/ReportByWallets';

type MainTab = 'bySales' | 'byStores' | 'byDishes' | 'byWallets';
export type TimeScale = 'day' | 'week' | 'month';

const Reports = () => {
  const { lang } = useLocale();
  const { isSm } = useMedia();
  const [mainTab, setMainTab] = useState<MainTab>('bySales');
  const [dateTab, setDateTab] = useState<TimeScale>('day');
  const [timeRange, setTimeRange] = useState<[number, number]>(getDayTimeRange());

  useEffect(() => {
    if (dateTab === 'day') {
      setTimeRange(getDayTimeRange());
    } else if (dateTab === 'week') {
      setTimeRange(getWeekTimeRange());
    } else {
      setTimeRange(getMonthTimeRange());
    }
  }, [dateTab]);

  return (
    <NivoProvider>
      <Box sx={{ maxWidth: 1000, m: '0 auto', pb: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Tabs
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            value={mainTab}
            onChange={(e, v) => setMainTab(v)}
          >
            <Tab label={t.report_bySales[lang]} value="bySales" />
            <Tab label={t.report_byStores[lang]} value="byStores" />
            <Tab label={t.report_byDishes[lang]} value="byDishes" />
            <Tab label={t.report_byWallets[lang]} value="byWallets" />
          </Tabs>
        </Box>

        {mainTab !== 'byDishes' && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexDirection: isSm ? 'column' : 'row',
              mb: 3,
              gap: 3,
            }}
          >
            <Tabs
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              value={dateTab}
              textColor="secondary"
              indicatorColor="secondary"
              onChange={(e, v) => setDateTab(v)}
            >
              <Tab label={t.per_day[lang]} value="day" />
              <Tab label={t.per_week[lang]} value="week" />
              <Tab label={t.per_month[lang]} value="month" />
            </Tabs>

            {dateTab === 'day' && (
              <Box sx={{ width: 180 }}>
                <DatePicker
                  label={t.date[lang]}
                  value={timeRange[0]}
                  onChange={(newValue) =>
                    newValue && setTimeRange([getDayDateRange(newValue).start, getDayDateRange(newValue).end])
                  }
                  renderInput={(params) => <StyledTextField fullWidth size="small" {...params} />}
                  disableFuture
                />
              </Box>
            )}

            {dateTab !== 'day' && <DateRangeButtons range={timeRange} setRange={setTimeRange} scale={dateTab} />}
          </Box>
        )}
        {mainTab === 'bySales' && <ReportBySales timeRange={timeRange} />}
        {mainTab === 'byDishes' && <ReportByDishes timeRange={timeRange} />}
        {mainTab === 'byStores' && <ReportByStores timeRange={timeRange} timeScale={dateTab} />}
        {mainTab === 'byWallets' && <ReportByWallets timeRange={timeRange} timeScale={dateTab} />}
      </Box>
    </NivoProvider>
  );
};

export default Reports;
